import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';

const ConfirmContext = createContext(null);

const VARIANTI = {
  danger:  'bg-red-500 hover:bg-red-600 text-white border-red-600',
  primary: 'bg-primary hover:opacity-90 text-primary-foreground border-primary',
};

export const ConfirmProvider = ({ children }) => {
  const [stato, setStato] = useState(null); // { opzioni, risolvi }
  const risolviRef = useRef(null);

  const chiudi = useCallback((risultato) => {
    const risolvi = risolviRef.current;
    risolviRef.current = null;
    setStato(null);
    if (risolvi) risolvi(risultato);
  }, []);

  const conferma = useCallback((opzioni = {}) => new Promise((risolvi) => {
    // se ne arriva una nuova mentre una è aperta, la precedente si considera annullata
    if (risolviRef.current) risolviRef.current(false);
    risolviRef.current = risolvi;
    setStato({ opzioni });
  }), []);

  useEffect(() => {
    if (!stato) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        chiudi(false);
      }
    };
    // capture: l'Esc chiude solo la conferma e non il modal sottostante
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [stato, chiudi]);

  useEffect(() => () => {
    if (risolviRef.current) risolviRef.current(false);
  }, []);

  const valore = useMemo(() => conferma, [conferma]);
  const o = stato?.opzioni;
  const variante = o?.variante === 'primary' ? 'primary' : 'danger';

  return (
    <ConfirmContext.Provider value={valore}>
      {children}
      {stato && (
        <div className="fixed inset-0 z-[350] flex items-center justify-center p-4" role="alertdialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => chiudi(false)}
          />
          <div className="relative w-full max-w-sm bg-card border border-border/60 rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="h-1 w-full bg-gradient-to-r from-primary via-primary/50 to-transparent" />
            <div className="p-7 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                {variante === 'danger' && (
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                    <Icon name="AlertTriangle" size={20} />
                  </div>
                )}
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-base font-black uppercase tracking-tight text-foreground break-words">
                    {o.titolo || 'Confermi?'}
                  </span>
                  {o.descrizione && (
                    <span className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest break-words">
                      {o.descrizione}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => chiudi(false)}
                  className="flex-1 px-4 py-2.5 rounded-2xl bg-muted hover:bg-muted/80 border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest transition-all"
                >
                  {o.annullaLabel || 'Annulla'}
                </button>
                <button
                  type="button"
                  autoFocus
                  onClick={() => chiudi(true)}
                  className={`flex-1 px-4 py-2.5 rounded-2xl border text-[11px] font-black uppercase tracking-widest transition-all ${VARIANTI[variante]}`}
                >
                  {o.confermaLabel || 'Conferma'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

/** Ritorna una funzione: await confirm({ titolo, descrizione, confermaLabel, annullaLabel, variante }) -> boolean */
export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve essere usato dentro ConfirmProvider');
  return ctx;
};
